import { FiatPrice } from "@b2bportal/air-booking-api";
import { set } from "lodash-es";
import { StateValue } from "xstate";

/**
 * ------------------------------------------------------
 * Parent state = outer most state
 * child states => nested 1 level
 * nestedChild states => nested 2 levels
 * ------------------------------------------------------
 * eg: Machine config
 *  "states": {
 *       "parentState": {
 *         "states": {
 *           "childState1": {
 *              "states": {
 *                "nestedChildState1": {}
 *              }
 *            },
 *           "childState2": {}
 *         }
 *       }
 *     }
 * ------------------------------------------------------
 * eg: State value
 * { "parentState": "childState1" }
 * ------------------------------------------------------
 **/

export const getParentState = (state: StateValue): StateValue | undefined => {
  if (typeof state === "object") {
    return Object.keys(state)[0];
  }
  return state;
};

export const getChildState = (state: StateValue): StateValue | undefined => {
  if (typeof state === "object") {
    const key = Object.keys(state)[0];
    const childState = key ? state[key] : undefined;
    // If there's nested states, just return the first key
    // Parallel states are not handled
    if (childState && typeof childState === "object") {
      return Object.keys(childState)[0];
    }
    // childState => string
    return childState;
  }
  // If type is string, no child state present
  return undefined;
};

export const getNestedChildState = (
  state: StateValue
): StateValue | undefined => {
  if (typeof state === "object") {
    const key = Object.keys(state)[0];
    const childState = key ? state[key] : undefined;
    if (childState && typeof childState === "object") {
      const nestedChildKey = Object.keys(childState)[0];
      return nestedChildKey ? childState[nestedChildKey] : undefined;
    }
  }
  return undefined;
};

export const setContextWithKey = <T extends object, V>(
  ctx: T,
  key: string,
  value: V
): T => set(ctx, key, value);

export const combinePromises = (promises: Array<Promise<unknown>>) =>
  Promise.all(promises);

type IObjectKeyTypes<T extends object> = {
  [K in keyof T]: K;
};
export const getObjectKeysAsObject = <T extends object>(
  genericObject: T
): IObjectKeyTypes<T> =>
  Object.keys(genericObject).reduce(
    (prev, cur) => ({ ...prev, [cur]: cur }),
    {} as IObjectKeyTypes<T>
  );

type FiatPriceWithoutSymbol = Omit<FiatPrice, "currencySymbol">;
export const getHasPriceChanged = (
  allowed: FiatPriceWithoutSymbol,
  actual: FiatPriceWithoutSymbol
) => {
  if (allowed.currencyCode === actual.currencyCode) {
    return actual.value >= allowed.value;
  } else {
    if (actual.currencyCode === "USD") {
      return actual.value >= 1;
    }
    if (actual.currencyCode === "COP") {
      return actual.value >= 5000;
    }
    if (actual.currencyCode === "GBP") {
      return actual.value >= 1;
    }
    // unknown currency, return false till we understand which currency and adjust default above
    return false;
  }
};
